import { useEffect, useState } from 'react';
import { getUsers } from '../../../services/api';
import {
  getBillingJobs,
  getBillingTransactions,
  getInvoices,
} from '../../../services/billingService';
import { getTeamsByClient } from '../../../services/teamService';
import type { JobRecord } from '../ItemizedLedgerTable';

export interface Invoice {
  id: string;
  period_end: string;
  total_amount: number;
  currency: string;
  status: string;
  credits_billed?: number;
  cost_per_credit?: number;
  costsByFunction?: Record<string, number>;
  costsByTeam?: Record<string, number>;
  costsByUser?: Record<string, number>;
}

export const useBillingData = (targetClientId: string | undefined, costPerCredit: number = 0) => {
  const [activeUserCount, setActiveUserCount] = useState<number | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [transactions, setTransactions] = useState<JobRecord[]>([]);
  const [usersMap, setUsersMap] = useState<Record<string, string>>({});
  const [teamsMap, setTeamsMap] = useState<Record<string, string>>({});
  const [loadingData, setLoadingData] = useState(false);

  useEffect(() => {
    if (targetClientId) {
      setLoadingData(true);

      getUsers({ companyId: targetClientId, limit: 1000 })
        .then((res) => {
          if (res.users) {
            const active = res.users.filter((u) => !u.disabled && u.status !== 'pending').length;
            setActiveUserCount(active);
            const uMap: Record<string, string> = {};
            for (const u of res.users) {
              uMap[u.uid] = u.displayName || u.email || u.uid;
            }
            setUsersMap(uMap);
          }
        })
        .catch(() => {
          // ignore error
        });

      getTeamsByClient(targetClientId)
        .then((teams) => {
          const tMap: Record<string, string> = {};
          for (const team of teams) {
            tMap[team.id] = team.teamName || team.id;
          }
          setTeamsMap(tMap);
        })
        .catch(() => {
          // ignore error
        });

      getInvoices(targetClientId, 12)
        .then((invoicesData) => {
          setInvoices(invoicesData as Invoice[]);
        })
        .catch(() => {
          // ignore error
        });

      Promise.all([
        getBillingJobs(targetClientId, 10000),
        getBillingTransactions(targetClientId, 10000),
      ])
        .then(([jobsData, txData]) => {
          const combined = [...(jobsData as JobRecord[]), ...(txData as JobRecord[])];

          combined.sort((a, b) => {
            const getMs = (tx: JobRecord) => {
              const tsValue = tx.startedAt || tx.updatedAt || tx.timestamp;
              if (
                tsValue &&
                typeof tsValue === 'object' &&
                'toDate' in tsValue &&
                typeof (tsValue as { toDate: () => Date }).toDate === 'function'
              ) {
                return (tsValue as { toDate: () => Date }).toDate().getTime();
              }
              if (typeof tsValue === 'string' || typeof tsValue === 'number') {
                return new Date(tsValue).getTime();
              }
              return 0;
            };
            return getMs(b) - getMs(a);
          });

          setTransactions(combined);
        })
        .catch(() => {
          // ignore error
        })
        .finally(() => setLoadingData(false));
    }
  }, [targetClientId]);

  const costsByFunction: Record<string, number> = {};
  const costsByTeam: Record<string, number> = {};
  const costsByUser: Record<string, number> = {};
  const costsByTender: Record<string, number> = {};

  const resetIndex = transactions.findIndex((tx) => tx.action_type === 'rolling_cycle_reset');
  const currentCycleTransactions =
    resetIndex === -1 ? transactions : transactions.slice(0, resetIndex);

  if (currentCycleTransactions.length > 0) {
    for (const job of currentCycleTransactions) {
      if (
        job.status === 'refunded' ||
        job.isRefunded === true ||
        job.action_type === 'manual_topup' ||
        job.action_type === 'rolling_cycle_reset'
      ) {
        continue;
      }

      const fnKey = job.context_tag || job.action_type || 'unknown';
      const teamKey = job.team_id || 'unassigned';
      const userKey = job.user_id || 'system';
      const tenderKey =
        job.tender_id || (job.context_tag === 'people_cv' ? 'People Search' : 'unassigned');
      const cost = ((job.total_credits_burned ?? job.credits_burned) || 0) * costPerCredit;

      costsByFunction[fnKey] = (costsByFunction[fnKey] || 0) + cost;
      costsByTeam[teamKey] = (costsByTeam[teamKey] || 0) + cost;
      costsByUser[userKey] = (costsByUser[userKey] || 0) + cost;
      costsByTender[tenderKey] = (costsByTender[tenderKey] || 0) + cost;
    }
  }

  return {
    activeUserCount,
    invoices,
    transactions,
    usersMap,
    teamsMap,
    loadingData,
    costsByFunction,
    costsByTeam,
    costsByUser,
    costsByTender,
  };
};
